<template>
  <body-viewer
    v-if="recordToShow && !measurementFailed"
    :record="recordToShow"
    @back="leave"
    @show-record="openRecordViewer"
  />
  <div v-else class="result-root relative w-full flex flex-col">
    <div class="flex-1 relative" :style="containerStyle">
      <model-viewer
        v-if="cachedObjUrl"
        bg-white
        class="h-full"
        :obj-url="cachedObjUrl"
        :shader-type="shaderType"
        :custom-shader="customShader"
        :measurement="currentMeasurement"
      />
    </div>
    <panel v-if="!measurementFailed" :height="panelHeight">
      <div class="w-full h-full text-center purple-bg p-10 flex-centered">
        <p class="scan-result-message" v-html="$t('result.analyzing')"></p>
      </div>
    </panel>
    <rounded-card v-else no-head :height="panelHeight">
      <error />
      <div v-if="showDoll" class="mini-doll flex flex-col my-5 justify-center">
        <p class="raise-hint" v-html="$t('result.raise')"></p>
        <doll
          :left="left"
          :right="right"
          :left-range="leftRange"
          :right-range="rightRange"
          mini
          left-breathing
          right-breathing
          @left-click="handleLeftClick"
          @right-click="handleRightClick"
          @left-hit="handleLeftHit"
          @right-hit="handleRightHit"
        />
      </div>
      <div v-else-if="!isContactless" class="w-full flex mt-auto mb-5 justify-around">
        <c-button width="40%" secondary class="my-3" @click="leave">
          <span>{{ $t('result.leave') }}</span>
        </c-button>
        <c-button v-if="!isMobileScan" width="40%" class="my-3" @click="retry">
          <span>{{ $t('result.scan_again') }}</span>
        </c-button>
      </div>
    </rounded-card>
    <leave-button v-if="canLeave" @click="leave" />
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';

import audioPlayer from '@/mixins/audio-player';
import scanResults from '@/mixins/scan-results';

import ScanProcess from '@/modules/scan-process';

import ModelViewer from '@/components/common/ModelViewer.vue';
import Panel from '@/components/common/Panel.vue';
import Doll from '@/components/common/Doll.vue';
import Error from '@/components/ShowResult/Error.vue';
import LeaveButton from '@/components/common/LeaveButton.vue';
import RoundedCard from '@/components/common/RoundedCard.vue';
import CButton from '@/components/common/CButton.vue';

import BodyViewer from '@/components/DefaultCloudzet/HomePage/BodyViewer.vue';

export default {
  name: 'ShowResult',
  components: {
    LeaveButton,
    BodyViewer,
    Panel,
    ModelViewer,
    RoundedCard,
    Error,
    Doll,
    CButton,
  },
  data() {
    return {
      cachedObjUrl: null,
      recordToShow: null,
    };
  },
  mixins: [audioPlayer, scanResults],
  mounted() {
    // Cache the original objUrl from user mixin to prevent reload obj
    // The objUrl will be overwritten during getAllKindsOfScanRecords
    this.cachedObjUrl = this.objUrl;
    this.playAudio('16-Almost_there');
  },
  computed: {
    ...mapState('app', ['scannersConfigs']),
    ...mapState('mtmUser', {
      mtmShaderType: state => state.shaderType,
      customShader: 'customShader',
    }),
    ...mapState('user', {
      userShaderType: state => state.shaderType,
    }),
    ...mapGetters('mtmUser', {
      hasMtmUserLoggedIn: 'isAuthenticated',
    }),
    ...mapGetters('user', ['latestBodyRecord']),
    shaderType() {
      if (this.hasMtmUserLoggedIn) return this.mtmShaderType;

      if (ScanProcess.scannerId && this.scannersConfigs[ScanProcess.scannerId]) {
        return this.scannersConfigs[ScanProcess.scannerId].shader_type;
      }

      return this.userShaderType;
    },
    measurementLayout() {
      return (!this.isContactless && 'white') || '';
    },
    canLeave() {
      return !this.nothingYet && !this.isContactless && !this.measurementFailed;
    },
    isMobileScan() {
      return ScanProcess.type === 'mobile';
    },
  },
  watch: {
    hasMeasurements(nv) {
      if (nv) {
        this.playAudio('17-3D_Scan_Successful', true);
        this.getAllKindsOfScanRecords().then(() => {
          this.recordToShow = this.latestBodyRecord;
        });
      }
    },
    measurementFailed(nv, ov) {
      if (nv && !ov && this.isContactless) {
        this.playAudio('18-3D_scan_unsuccessful', true);
      }
    },
  },
  methods: {
    ...mapActions('user', ['getAllKindsOfScanRecords']),
    openRecordViewer(record) {
      this.recordToShow = record;
    },
  },
};
</script>

<style lang="scss" scoped>
.result-root {
  background-color: #fff;
}
.purple-bg {
  background-image: linear-gradient(to right, #2245ff, #540deb);
  color: #fff;
  .scan-result-message {
    color: #fff;
  }
}
.scan-result-message {
  @apply text-xl font-bold;
  @screen md {
    @apply text-3xl;
    line-height: 60px;
  }
  line-height: 1.67;
}
.mini-doll {
  overflow: hidden;
}
.raise-hint {
  @apply text-2xl text-center px-2 mx-auto my-3;
  color: #111;
}
</style>
